import React, { useEffect, useMemo, useState } from 'react';
import useAutoScroll from '../../hooks/useAutoScroll';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useStudio } from '../../Context/StudioContext';
import { DESIGN_PRESETS } from '../../Context/DesignPresets';
import CardMusicPlayer from '../CardView/CardMusicPlayer';
import MainPageCard from '../CardView/MainPageCard';
import WeddingDetails from '../CardView/WeddingDetails';
import CardBottomNav from '../CardView/CardBottomNav';
import CountingDay from '../CardView/CountingDay';
import GalleryCarousel from '../CardView/GalleryCarousel';
import WishSection from '../CardView/WishSection';
import CardOpening from '../CardView/CardOpening';

import { dummyWishes } from '../../Context/DummyWishesPreview'
import CardParticles from '../CardView/CardParticles';
import { PREVIEW_MUSIC_LIST } from '../../Context/PreviewMusicList';
import IMG1 from '../../Components/Designs/demo-bridePhotoExample1.jpg';
import IMG2 from '../../Components/Designs/demo-bridePhotoExample2.jpg';
import IMG3 from '../../Components/Designs/demo-bridePhotoExample3.jpg';
import IMG01 from '../../Components/Designs/whenLifeGiveYouTangerine-01.jpg';
import IMG02 from '../../Components/Designs/whenLifeGiveYouTangerine-02.jpg';
import IMG03 from '../../Components/Designs/whenLifeGiveYouTangerine-03.jpg';
import IMG04 from '../../Components/Designs/start-up-01.jpg';
import IMG05 from '../../Components/Designs/start-up-02.jpg';
import IMG06 from '../../Components/Designs/start-up-03.jpg';

import '../../CSS/rsvpPopup.css';

const DesignPreviewRender = () => {
    const { designCode } = useParams();
    const [searchParams] = useSearchParams();
    const { formData, setFormData } = useStudio();
    const [cardOpened, setCardOpened] = useState(false);
    const [musicReady, setMusicReady] = useState(false);
    const samplePackage = searchParams.get('samplePackage');
    const QR = searchParams.get('QR');
    const wishlistGift = searchParams.get('gift');
    const [bannerVisible, setBannerVisible] = useState(true);
    // Pick a random music track for preview
    const randomMusic = useMemo(() => {
        const randomIndex = Math.floor(Math.random() * PREVIEW_MUSIC_LIST.length);
        if (designCode === "kdrama-001") {
            return PREVIEW_MUSIC_LIST[0];
        }

        return PREVIEW_MUSIC_LIST[randomIndex];
    }, []);

    useAutoScroll(cardOpened);

    // Fade out the preview banner after 5 seconds
    useEffect(() => {
        const timer = setTimeout(() => setBannerVisible(false), 4000);
        return () => clearTimeout(timer);
    }, []);

    // Hide navbar/footer like the actual card does
    useEffect(() => {
        const navbar = document.querySelector('.main-navbar');
        const footer = document.querySelector('.main-footer');
        if (navbar) navbar.style.display = 'none';
        if (footer) footer.style.display = 'none';
        return () => {
            if (navbar) navbar.style.display = '';
            if (footer) footer.style.display = '';
        };
    }, []);

    // Sync the designCode from URL into context
    useEffect(() => {
        if (designCode) {
            setFormData(prev => ({ ...prev, designCode }));
        }
    }, [designCode]);

    // Look up the design preset for the current designCode
    const preset = DESIGN_PRESETS[designCode];

    // Merge: formData (sample content) + preset (theme/colors/background)
    // Preset fields override formData defaults so the right design shows
    const cardData = useMemo(() => {
        const currentPackage = samplePackage || formData.packageType;
        const isCurrentlyBasic = currentPackage === 'Basic';

        return {
            ...formData,
            ...preset,

            // Map both keys so downstream components work regardless of which field they check.
            packageType: currentPackage,
            showQRBank: QR || formData.showQRBank,
            showWishlistGift: wishlistGift || formData.showWishlistGift,
            // If music properties are empty (StudioContext default), use random music
            musicYoutubeURL: formData.musicYoutubeURL || randomMusic.musicYoutubeURL,
            musicTitle: formData.musicTitle || randomMusic.musicTitle,
            musicStartDuration: formData.musicStartDuration || randomMusic.musicStartDuration,
            // Fallbacks if neither formData nor preset has these fields
            themeBaseCardColour: preset?.themeBaseCardColour ?? formData.themeBaseCardColour ?? '#ffffff',
            themeColour: preset?.themeColour ?? formData.themeColour ?? formData.fontColor ?? '#FF238a',
            showOnlyWishes: isCurrentlyBasic ? true : formData.showOnlyWishes,
            showRSVP: isCurrentlyBasic ? false : formData.showRSVP,
        };
    }, [formData, preset, randomMusic, samplePackage, QR, wishlistGift]);



    // GalleryCarousel uses src={img} so it expects plain strings — same as
    // what getCardImages() returns (see cardService.js line 102)
    const previewImages = (
        designCode === "kdrama-001" ? [IMG01, IMG02, IMG03] :
            designCode === "kdrama-007" ? [IMG04, IMG05, IMG06]
                : [IMG1, IMG2, IMG3]
    ).filter(Boolean);

    // CardBottomNav expects a `contacts` array — map from formData.contactDetails
    const contacts = formData.contactDetails ?? [];

    // WeddingDetails expects a `programmes` array — already correct in formData
    const programmes = formData.programmes ?? [];

    const isClassicTemplate = cardData.designTemplate === 'classic';

    const theme = useMemo(() => createTheme({
        typography: {
            fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
        },
        components: {
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                                borderColor: cardData.themeColour,
                            },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: cardData.themeColour,
                        },
                    },
                },
            },
        },
    }), [cardData.themeColour]);

    // Guard — unknown design code → show Page Not Found
    if (!preset) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    backgroundColor: '#f5f5f5',
                }}
            >
                <Typography variant="h5" sx={{ fontFamily: 'Poppins', fontWeight: 500, color: '#292929ff' }}>
                    Design Not Found
                </Typography>
                <Typography sx={{ fontFamily: 'Poppins', fontSize: '14px', color: '#888' }}>
                    No design exists for code: <strong>{designCode}</strong>
                </Typography>
            </Box>
        );
    }


    return (
        <ThemeProvider theme={theme}>
            <div
                style={{
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    overflowY: 'auto',
                }}
            >
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: '450px',
                        ...(isClassicTemplate
                            ? {
                                backgroundImage: `linear-gradient(rgba(226, 128, 47, 0.38)), url(/card-backgrounds/klasikFullBackground.jpg)`,
                                backgroundSize: '100% 100%',
                            }
                            : {
                                backgroundColor: cardData.themeBaseCardColour,
                            }),
                        boxShadow: '0 0 10px rgba(165, 13, 13, 0.1)',
                    }}
                >

                    {/* ── Opening / Envelope animation ───────────────────────────── */}
                    <CardOpening
                        data={cardData}
                        openingStyle={cardData.openingStyle ?? 'Tingkap A'}
                        openingColor={cardData.openingColor ?? '#f2f2f2'}
                        onOpen={() => setCardOpened(true)}
                        musicReady={musicReady}
                    >
                        <CardParticles data={cardData} />
                        <CardMusicPlayer data={cardData} playOnOpen={cardOpened} onMusicReady={() => setMusicReady(true)} />
                        <MainPageCard data={cardData} isOpened={cardOpened} />
                        <CardBottomNav data={cardData} contacts={contacts} />
                    </CardOpening>

                    {/* ── Rest of the card (shown after opening) ──────────────────── */}
                    {cardOpened && (
                        <>
                            <WeddingDetails data={cardData} programmes={programmes} />
                            <CountingDay data={cardData} images={previewImages} />
                            {previewImages.length > 0 && cardData.packageType !== 'Basic' && (
                                <GalleryCarousel images={previewImages} />
                            )}
                            <WishSection data={cardData} previewWishes={dummyWishes} disableLive={true} />
                            <Box sx={{ px: 2, textAlign: 'center', pb: '110px', display: 'block' }}>


                                <Typography sx={{ fontFamily: 'Poppins', fontSize: '13px' }} color={designCode === "floral-007" ? '#fefefe' : 'text.secondary'}>
                                    Created by
                                </Typography>

                                <Box
                                    component="a"
                                    href="https://www.sejiwa-my.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ justifyContent: "center", display: "flex", alignItems: "center", gap: 1, textDecoration: "none" }}
                                >
                                    <Box
                                        component="img"
                                        src="/sejiwa-logo-2.png"
                                        alt="Sejiwa"
                                        sx={{ width: "30px", height: "30px" }}
                                    />
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontFamily: '"Fredoka", "Inter", sans-serif',
                                            fontWeight: 400,
                                            letterSpacing: "1.5px",
                                            color: "#45C0C0",
                                        }}
                                    >
                                        Sejiwa
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    )}
                </div>

                {/* ── Preview banner so it's obvious this is a demo ───────────── */}
                <div
                    style={{
                        position: 'fixed',
                        opacity: bannerVisible ? 0.3 : 0,
                        top: 450,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(90deg,#6d1826,#c0395a)',
                        color: '#fff',
                        textAlign: 'center',
                        padding: '6px 0',
                        fontSize: '12px',
                        fontFamily: 'Poppins, sans-serif',
                        zIndex: 9999,
                        letterSpacing: '1px',
                        transition: 'opacity 1s ease',
                        pointerEvents: 'none',
                    }}
                >
                    DESIGN PREVIEW — {designCode} — Data is sample only
                    <br />
                    Guest Wish & RSVP are not functional in preview mode
                </div>
            </div>
        </ThemeProvider>
    );
};

export default DesignPreviewRender;